import {
  collection,
  query,
  where,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import type { ChatConversation } from '@/types/liveChat';

const CONVERSATIONS_COL = 'chat_conversations';

// Notification sound (short subtle chime)
let audioCtx: AudioContext | null = null;

/**
 * The original subtle live-chat chime (880Hz → 440Hz sweep). Exported so the
 * soundboard listener can reuse the exact same tone for follow-up customer
 * messages while the first message of a conversation gets the configured
 * "initial" event sound.
 *
 * @param volumeScale Multiplier applied to the base gain (0.15). Defaults to 1.
 */
export function playLiveChatChime(volumeScale = 1) {
  try {
    if (!audioCtx) audioCtx = new AudioContext();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.frequency.setValueAtTime(880, audioCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, audioCtx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.15 * volumeScale, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.3);
    osc.start(audioCtx.currentTime);
    osc.stop(audioCtx.currentTime + 0.3);
  } catch {
    // Audio not available
  }
}

function playNotificationSound() {
  playLiveChatChime();
}

export function requestBrowserNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

export function showBrowserNotification(title: string, body: string, tag?: string) {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, { body, icon: '/FINLOGO.png', tag: tag || 'ccoc-chat', renotify: false });
  }
}

/**
 * Subscribe to live chat conversations for the tab badge.
 * Returns total open count and unread count separately.
 */
export function subscribeUnreadLiveChatCount(
  callback: (total: number, unread: number) => void
) {
  const q = query(collection(db, CONVERSATIONS_COL));

  const notifiedConvIds = new Map<string, number>();
  let lastNotificationAt = 0;

  return onSnapshot(
    q,
    (snapshot) => {
      const openConversations = snapshot.docs.filter((d) => {
        const data = d.data();
        return data.status === 'active' || data.status === 'waiting';
      });

      const unreadConversations = openConversations.filter((d) => d.data().unreadByStaff === true);
      const unreadCount = unreadConversations.length;

      const now = Date.now();

      const newUnread = unreadConversations.filter((d) => {
        const lastNotified = notifiedConvIds.get(d.id);
        if (!lastNotified) return true;
        const data = d.data();
        const lastCustomerMsg = (data as any).lastCustomerMessageAt?.toMillis?.() ?? 0;
        return lastCustomerMsg > lastNotified;
      });

      if (newUnread.length > 0 && now - lastNotificationAt > 30000) {
        lastNotificationAt = now;
        playNotificationSound();

        const newest = newUnread
          .map((d) => ({ id: d.id, ...d.data() as ChatConversation }))
          .sort((a, b) => {
            const aTime = (a as any).lastCustomerMessageAt?.toMillis?.() ?? 0;
            const bTime = (b as any).lastCustomerMessageAt?.toMillis?.() ?? 0;
            return bTime - aTime;
          })[0];

        if (newest) {
          showBrowserNotification(
            `New CCOC from ${newest.customerName || 'Customer'}`,
            'A new live chat has been started',
            `ccoc-chat-${newest.id}`
          );
        }

        newUnread.forEach((d) => notifiedConvIds.set(d.id, now));
      }

      // Only clean up resolved/closed conversations
      const activeIds = new Set(snapshot.docs.map((d) => d.id));
      for (const id of notifiedConvIds.keys()) {
        const doc = snapshot.docs.find((d) => d.id === id);
        if (!doc || !activeIds.has(id)) {
          notifiedConvIds.delete(id);
          continue;
        }
        const status = doc.data().status;
        if (status === 'resolved') {
          notifiedConvIds.delete(id);
        }
      }

      callback(openConversations.length, unreadCount);
    },
    (error) => {
      console.error('[LiveChat] subscribeUnreadLiveChatCount error:', error);
      callback(0, 0);
    }
  );
}

/**
 * Subscribe to all unread live chat conversations (for use inside the Live Chat tab).
 */
export function subscribeActiveLiveChatConversations(
  statusFilter: string | null,
  callback: (conversations: ChatConversation[]) => void
) {
  let q;
  if (statusFilter && statusFilter !== 'all') {
    q = query(
      collection(db, CONVERSATIONS_COL),
      where('status', '==', statusFilter),
      // orderBy handled client-side for flexibility
    );
  } else {
    q = query(collection(db, CONVERSATIONS_COL));
  }

  return onSnapshot(
    q,
    (snapshot) => {
      const conversations = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as ChatConversation[];
      callback(conversations);
    },
    (error) => {
      console.error('[LiveChat] subscribeActiveLiveChatConversations error:', error);
      callback([]);
    }
  );
}
